import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  UploadCloud, FileSpreadsheet, Download, Loader2, CheckCircle2,
  AlertTriangle, XCircle, Copy, ArrowLeft,
} from "lucide-react";

const TEMPLATE_HEADERS = ["Name", "Email", "Employee Code", "Phone", "Department", "Address Line 1", "Address Line 2", "Landmark", "City", "State/Province", "Postal Code", "Country"];
const TEMPLATE_SAMPLE = ["Jane Doe", "jane.doe@example.com", "EMP0100", "+91 90000 00000", "Engineering", "Flat 4B, Example Towers", "12 Sample Street", "Opposite Fictional Park", "Bengaluru", "Karnataka", "560001", "India"];
const csvCell = (v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

const STATUS_STYLE = {
  VALID: { icon: CheckCircle2, cls: "text-emerald-600", label: "Valid" },
  WARNING: { icon: AlertTriangle, cls: "text-amber-600", label: "Warning" },
  ERROR: { icon: XCircle, cls: "text-red-600", label: "Error" },
  DUPLICATE: { icon: Copy, cls: "text-indigo-600", label: "Duplicate" },
};

export default function ImportEmployeesDialog({ open, onOpenChange, masters = [], companies = [], onImported }) {
  const [step, setStep] = useState("upload");   // upload | preview | done
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [master, setMaster] = useState("");
  const [subsidiary, setSubsidiary] = useState("");
  const [batch, setBatch] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [mapping, setMapping] = useState({});
  const [updateExisting, setUpdateExisting] = useState(false);
  const [preview, setPreview] = useState(null);   // {counts, preview[], row_count}
  const [result, setResult] = useState(null);      // commit summary
  const inputRef = useRef(null);

  const subs = companies.filter((c) => c.master_company_id === master);

  const reset = () => {
    setStep("upload"); setBusy(false); setDrag(false);
    setBatch(null); setSheet(null); setMapping({}); setPreview(null); setResult(null);
  };

  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const downloadTemplate = () => {
    const csv = [TEMPLATE_HEADERS.map(csvCell).join(","), TEMPLATE_SAMPLE.map(csvCell).join(",")].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "employee-import-template.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const doUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/excel-import/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const sheets = data.sheets || [];
      const chosen = sheets.find((s) => s.suggested_destination === "employees" && s.row_count > 0)
        || sheets.find((s) => s.row_count > 0) || sheets[0];
      if (!chosen) { toast.error("No data rows found in the file"); return; }
      setBatch(data); setSheet(chosen);
      // Auto-map columns to the employees destination
      const { data: am } = await api.get(`/excel-import/batch/${data.batch_id}/automap`, {
        params: { sheet: chosen.name, destination: "employees" },
      });
      setMapping(am.mapping || {});
      // Validate for a preview
      const { data: v } = await api.post(`/excel-import/batch/${data.batch_id}/validate`, {
        sheet: chosen.name, destination: "employees", mapping: am.mapping || {},
      });
      setPreview(v);
      setStep("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const doImport = async () => {
    if (!batch || !sheet) return;
    setBusy(true);
    try {
      const overrides = {};
      if (master) overrides.master_company_id = master;
      if (subsidiary) overrides.company_id = subsidiary;
      const { data } = await api.post(`/excel-import/batch/${batch.batch_id}/commit`, {
        sheet: sheet.name, destination: "employees", mapping,
        dup_strategy: updateExisting ? "UPDATE" : "SKIP", overrides,
      });
      setResult(data.summary);
      setStep("done");
      toast.success(`Imported ${data.summary.created} employee(s)`);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const onPick = (e) => doUpload(e.target.files?.[0]);
  const onDrop = (e) => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files?.[0]); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl" data-testid="emp-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-emerald-600" /> Import employees from Excel
          </DialogTitle>
          <DialogDescription>
            Upload an Excel / CSV file. Columns are auto-mapped, previewed, and only imported after you confirm. Duplicates (same email) are skipped.
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            {/* Company scope */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Master company (optional)</label>
                <Select value={master} onValueChange={(v) => { setMaster(v); setSubsidiary(""); }}>
                  <SelectTrigger className="h-9 mt-1" data-testid="emp-import-master"><SelectValue placeholder="My organisation (default)" /></SelectTrigger>
                  <SelectContent>
                    {masters.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Subsidiary (optional)</label>
                <Select value={subsidiary} onValueChange={setSubsidiary} disabled={!master || subs.length === 0}>
                  <SelectTrigger className="h-9 mt-1" data-testid="emp-import-subsidiary"><SelectValue placeholder={subs.length ? "Select subsidiary" : "None"} /></SelectTrigger>
                  <SelectContent>
                    {subs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${drag ? "border-emerald-500 bg-emerald-50" : "border-slate-300 hover:border-slate-400"}`}
              data-testid="emp-import-dropzone"
            >
              <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onPick} data-testid="emp-import-file" />
              {busy ? (
                <div className="flex flex-col items-center gap-2 text-slate-500">
                  <Loader2 className="h-7 w-7 animate-spin" /> <span className="text-sm">Reading & validating…</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <UploadCloud className="h-8 w-8 text-slate-400" />
                  <div className="text-sm font-medium text-slate-700">Drop your file here or click to browse</div>
                  <div className="text-xs text-slate-400">.xlsx, .xls or .csv — up to 25MB</div>
                </div>
              )}
            </div>

            <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:underline" data-testid="emp-import-template">
              <Download className="h-4 w-4" /> Download Excel template
            </button>
          </div>
        )}

        {step === "preview" && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-2">
              {["VALID", "WARNING", "DUPLICATE", "ERROR"].map((k) => {
                const S = STATUS_STYLE[k];
                return (
                  <div key={k} className="rounded-md border border-slate-200 p-2 text-center" data-testid={`emp-import-count-${k.toLowerCase()}`}>
                    <div className={`flex items-center justify-center gap-1 ${S.cls}`}><S.icon className="h-4 w-4" /><span className="text-lg font-semibold">{preview.counts?.[k] ?? 0}</span></div>
                    <div className="text-[11px] text-slate-500">{S.label}</div>
                  </div>
                );
              })}
            </div>
            <div className="text-xs text-slate-500">{preview.row_count} row(s) in <span className="font-medium">{sheet?.name}</span>. Duplicates and errors are skipped on import.</div>
            {!(preview.counts?.VALID || preview.counts?.WARNING) && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700" data-testid="emp-import-nothing">
                No new employees to import — every row is a duplicate or has errors. Choose another file or fix the highlighted rows.
              </div>
            )}
            <div className="max-h-64 overflow-auto rounded-md border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-500">
                    <th className="px-2 py-1.5">Row</th><th className="px-2 py-1.5">Name</th>
                    <th className="px-2 py-1.5">Email</th><th className="px-2 py-1.5">Address</th><th className="px-2 py-1.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(preview.preview || []).slice(0, 100).map((r) => {
                    const S = STATUS_STYLE[r.status] || STATUS_STYLE.VALID;
                    return (
                      <tr key={r.row}>
                        <td className="px-2 py-1 text-slate-400">{r.row}</td>
                        <td className="px-2 py-1">{r.data?.name || <span className="text-slate-300">—</span>}</td>
                        <td className="px-2 py-1 text-slate-600">{r.data?.email || <span className="text-slate-300">—</span>}</td>
                        <td className="px-2 py-1 text-slate-500 text-[11px]" data-testid={`emp-import-address-${r.row}`}>{[r.data?.address_line1, r.data?.city, r.data?.postal_code, r.data?.country].filter(Boolean).join(", ") || <span className="text-slate-300">—</span>}</td>
                        <td className="px-2 py-1"><span className={`inline-flex items-center gap-1 ${S.cls}`}><S.icon className="h-3.5 w-3.5" />{S.label}</span>{r.reason ? <div className="text-[10px] text-amber-700">{r.reason}</div> : null}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {step === "done" && result && (
          <div className="space-y-3 py-2 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <div className="text-lg font-semibold text-slate-800">Import complete</div>
            <div className="text-sm text-slate-600">
              <span className="font-semibold text-emerald-700">{result.created}</span> created ·{" "}
              <span className="font-medium">{result.skipped}</span> skipped ·{" "}
              <span className="font-medium">{result.updated || 0}</span> updated ·{" "}
              <span className="font-medium">{result.duplicates}</span> duplicates ·{" "}
              <span className="font-medium">{result.failed}</span> failed
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "preview" && (
            <>
              <Button variant="outline" onClick={reset} disabled={busy} data-testid="emp-import-back"><ArrowLeft className="h-4 w-4 mr-1" /> Choose another file</Button>
              <label className="flex items-center gap-2 text-[12px] text-slate-600 mr-auto"><input type="checkbox" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} data-testid="emp-import-update-existing" /> Update existing employees (matched by email) — filled cells overwrite, blank cells keep current values</label>
              <Button className="bg-slate-900" onClick={doImport} disabled={busy || !(preview?.counts?.VALID || preview?.counts?.WARNING || (updateExisting && preview?.counts?.DUPLICATE))} data-testid="emp-import-confirm">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Import {(preview?.counts?.VALID || 0) + (preview?.counts?.WARNING || 0)} employee(s)
              </Button>
            </>
          )}
          {step === "done" && (
            <Button className="bg-slate-900" onClick={() => close(false)} data-testid="emp-import-done">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
